import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Headphones, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Attraction } from '@underline/shared';
import { EmptyState } from '@/components/ui/empty-state';
import { AttractionFormModal } from '@/components/attractions/attraction-form-modal';
import { useAttractions, useDeleteAttraction } from '@/hooks/use-attractions';
import { useAuthStore } from '@/stores/auth-store';
import { TypedHeading } from '@/components/ui/typed-heading';

export function SettingsDjsPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const canManage = user?.role === 'admin' || user?.role === 'gerente' || user?.role === 'gestor';

  const { data: attractions = [] } = useAttractions();
  const delAttr = useDeleteAttraction();

  const [attrFormOpen, setAttrFormOpen] = useState(false);
  const [editAttr, setEditAttr] = useState<Attraction | null>(null);

  function handleDeleteAttr(a: Attraction) {
    if (!confirm(`Excluir ${a.name}?`)) return;
    delAttr.mutate(a.id, {
      onSuccess: () => toast.success('DJ excluído'),
      onError: (err) => toast.error(err.message),
    });
  }

  if (!canManage) {
    return (
      <div className="space-y-4">
        <header className="flex items-center gap-3">
          <button
            onClick={() => navigate('/settings')}
            aria-label="Voltar"
            className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <TypedHeading text="DJs" />
        </header>
        <EmptyState title="Sem permissão" description="Apenas gestores e admin." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate('/settings')}
            aria-label="Voltar"
            className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <TypedHeading text="DJs" />
        </div>
        <button
          onClick={() => {
            setEditAttr(null);
            setAttrFormOpen(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-red"
        >
          <Plus className="h-4 w-4" /> Adicionar
        </button>
      </header>

      <section className="rounded-2xl border border-border bg-card p-5 shadow-lift">
        {attractions.length === 0 ? (
          <EmptyState
            icon={<Headphones className="h-5 w-5" />}
            title="Nenhum DJ"
            description="Adicione o primeiro DJ para usá-lo no line-up dos eventos."
          />
        ) : (
          <ul className="divide-y divide-border">
            {attractions.map((a) => (
              <li key={a.id} className="flex items-center gap-3 py-3">
                <div className="h-10 w-10 rounded-full bg-muted grid place-items-center text-muted-foreground overflow-hidden shrink-0">
                  {a.photo_url ? (
                    <img
                      src={a.photo_url}
                      alt={a.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Headphones className="h-4 w-4" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{a.name}</p>
                  {a.instagram && (
                    <p className="mono text-[11px] uppercase text-muted-foreground truncate">
                      {a.instagram}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => {
                      setEditAttr(a);
                      setAttrFormOpen(true);
                    }}
                    aria-label="Editar"
                    className="h-9 w-9 grid place-items-center rounded-md hover:bg-muted"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteAttr(a)}
                    aria-label="Excluir"
                    className="h-9 w-9 grid place-items-center rounded-md hover:bg-destructive/10 text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <AttractionFormModal
        open={attrFormOpen}
        attraction={editAttr}
        onClose={() => {
          setAttrFormOpen(false);
          setEditAttr(null);
        }}
      />
    </div>
  );
}
